/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import User from "../../Models/UserModels/User";
import Overview from "./components/Overveiw";
import LandingReport from "./components/LandingReport";
import Landing from "../../Models/LandingModels/Landing";
import SendReportModal from "./components/SendReportModal";
import WeightNoteReport from "./components/WeightNoteReport";
import CatchReport from "./components/CatchReport";
import CatchNewsReport from "./components/CatchNewsReport";
import TransactionsReport from "./components/TransactionsReport";
import CatchSumReport from "./components/CatchSumReport";
import CatchValueReport from "./components/CatchValueReport";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid";
import { getCatchesReport, getCatchNewsReport, getCatchSumReport, getCatchValueReport, getLandingOverViewReport, getReportPDF, getTransactionsReport, getWeightNotesReport } from "../../Services/LondunarkerfiAPIService";
import API_PAGINATION_SIZE from "../../Constants/PaginationSize";
import { logError } from "../../Helpers/LogError";
import dayjs from "dayjs";
import CatchNews from "../../Models/ReportModels/CatchNews";
import WeightReport from "../../Models/ReportModels/WeightReport";
import CatchSum from "../../Models/ReportModels/CatchSum";
import CatchValue from "../../Models/ReportModels/CatchValue";
import transactions from "../../Models/ReportModels/Transactions";
import WeightNote from "../../Models/WeightNoteModels/WeightNote";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { LandingOverview } from "../../Models/ReportModels/LandingOverview";
import { FilterClause, FilterPayload } from "./components/Filters/FilterDefinition";
import FiltersDialog from "./components/Filters/FiltersDialog";
import "dayjs/locale/is";
dayjs.locale("is");

interface ReportsProps {
  user: User;
  showSnackbar: (message: string, severity: string) => void;
  toggleDashboard: (open: boolean) => void;
  refetchLandings: () => Promise<void>;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
}

const Reports: React.FC<ReportsProps> = (props) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [openSendReportModal, setOpenSendReportModal] = useState(false);
  const theme = useTheme();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [filterColumns, setFilterColumns] = useState<GridColDef[]>([]);
  const [filterModel, setFilterModel] = useState<FilterPayload>({ logic: "and", clauses: [] });
  const [focusIndex, setFocusIndex] = React.useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [excelLoading, setExcelLoading] = useState<boolean>(false);
  const apiRef = useGridApiRef();
  const [groupBy, setGroupBy] = React.useState<string[]>([]);
  const [groupOptions, setGroupOptions] = React.useState<{ label: string; field: string }[]>([]);
  const selectedGroupOptions = groupBy.flatMap(field => groupOptions.filter(option => option.field === field));
  const effectiveGroupBy = [...new Set(groupBy.map(field =>
    field === "shipNumber" || field === "shipName" || field === "shipRegistrationNumber" ? "ship" : field
  ))];

  const buildFileName = (
    title: string,
    harbourLabel?: string | number,
    ext: ".pdf" | ".xlsx" = ".pdf"
  ) => {
    const dateStamp = dayjs().format("DD.MM.YYYY"); // fetch date
    const harbour = harbourLabel ? `__${harbourLabel}` : "";
    // sanitize forbidden filename chars just in case
    const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]+/g, "-").trim();
    return `${sanitize(title)}${harbour}__${dateStamp}${ext}`.replace(/\s+/g, "_");
  };

  // Helper to format date as dd.mm.yyyy
  const formatDate = (date: Date) => {
    const d = date.getDate().toString().padStart(2, "0");
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };
  const [dateFrom, setDateFrom] = useState<string>(formatDate(dayjs().subtract(1, 'month').toDate()));
  const [dateTo, setDateTo] = useState<string>(formatDate(dayjs().toDate()));

  const PAGE_SIZE = API_PAGINATION_SIZE;

  // Store report data for each report type
  const [reportData, setReportData] = useState<{ [key: string]: unknown[] }>({});
  const activeCount = (filterModel.clauses ?? []).length;

  // Fetchers for each report type
  const fetchers: {
    [key: string]: (
      page: number,
      search: string,
      reset: boolean,
      filterModel?: FilterPayload
    ) => Promise<void>;
  } = {
    landingOverview: async (page, _search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getLandingOverViewReport(
          props.user.userPickedHarbour,
          page,
          dateFrom,
          dateTo,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          landingOverview: reset ? response : [...(prev.landingOverview || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    catchNews: async (page, _search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getCatchNewsReport(
          props.user.userPickedHarbour,
          page,
          dateFrom,
          dateTo,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          catchNews: reset ? response : [...(prev.catchNews || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    catch: async (page, search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getCatchesReport(
          props.user?.role?.id === Number(process.env.REACT_APP_ROLE_ADMIN_ID) ? undefined : props.user.userPickedHarbour,
          search,
          page,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          catch: reset ? response : [...(prev.catch || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    catchSum: async (_page, _search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getCatchSumReport(
          props.user.userPickedHarbour,
          dateFrom,
          dateTo,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          catchSum: reset ? response : [...(prev.catchSum || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    catchValue: async (_page, _search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getCatchValueReport(
          props.user.userPickedHarbour,
          dateFrom,
          dateTo,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          verdmaeti: reset ? response : [...(prev.verdmaeti || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    transactions: async (page, _search, reset, filterModel) => {
      setLoading(true);
      try {
        const response = await getTransactionsReport(
          props.user.userPickedHarbour,
          page,
          dateFrom,
          dateTo,
          filterModel
        );
        setReportData((prev) => ({
          ...prev,
          transactions: reset ? response : [...(prev.transactions || []), ...response],
        }));
        setHasMore(response.length === PAGE_SIZE);
      } catch (error) {
        logError(error);
      }
      setLoading(false);
    },
    weightNotes: async (page, search, reset, filterModel) => {
      try {
        setLoading(true);
        // Only pass isClosed if present in filterModel
        let isClosed: boolean | undefined = undefined;
        if (filterModel) {
          const closedFilter = filterModel.clauses.find(item => item.field === 'isClosed');
          if (closedFilter && closedFilter.value !== undefined && closedFilter.value !== null && closedFilter.value !== '') {
            isClosed = closedFilter.value === true || closedFilter.value === 'true';
          }
        }
        const response = await getWeightNotesReport(
          props.user.userPickedHarbour,
          search,
          page,
          isClosed,
          filterModel
        );
        setReportData(prev => ({
          ...prev,
          weightNotes: reset ? response : [...(prev.weightNotes || []), ...response]
        }));
        setHasMore(response.length === PAGE_SIZE);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        logError(error);
      }
    },
  };

  const fetchLandings = async (pageToFetch: number, reset: boolean, filterModel?: FilterPayload) => {
    try {
      setLoading(true);
      const response = await getLandingOverViewReport(
        props.user.userPickedHarbour,
        pageToFetch,
        dateFrom,
        dateTo,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        landingOverview: reset ? response : [...(prev.landingOverview || []), ...response]
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const fetchCatchNews = async (
    pageToFetch: number,
    reset: boolean,
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => {
    try {
      setLoading(true);
      const response = await getCatchNewsReport(
        props.user.userPickedHarbour,
        pageToFetch,
        dateFrom,
        dateTo,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        catchNews: reset ? response : [...(prev.catchNews || []), ...response]
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const fetchCatches = async (pageToFetch: number, searchText: string, reset: boolean, filterModel?: FilterPayload) => {
    try {
      setLoading(true);
      const response = await getCatchesReport(
        props.user.userPickedHarbour,
        searchText,
        pageToFetch,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        catch: reset ? response : [...(prev.catch || []), ...response]
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const fetchCatchSum = async (
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => {
    try {
      setLoading(true);
      const response = await getCatchSumReport(
        props.user.userPickedHarbour,
        dateFrom,
        dateTo,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        catchSum: response
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const fetchCatchValues = async (
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => {
    setLoading(true);
    try {
      const response = await getCatchValueReport(
        props.user.userPickedHarbour,
        dateFrom,
        dateTo,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        verdmaeti: response
      }));
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (
    pageToFetch: number,
    reset: boolean,
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => {
    try {
      setLoading(true);
      const response = await getTransactionsReport(
        props.user.userPickedHarbour,
        pageToFetch,
        dateFrom,
        dateTo,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        transactions: reset ? response : [...(prev.transactions || []), ...response]
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const fetchWeightNotes = async (pageToFetch: number, searchText: string, reset: boolean, filterModel?: FilterPayload) => {
    try {
      setLoading(true);
      // Only pass isClosed if present in filterModel
      let isClosed: boolean | undefined = undefined;
      if (filterModel) {
        const closedFilter = filterModel.clauses.find(item => item.field === 'isClosed');
        if (closedFilter && closedFilter.value !== undefined && closedFilter.value !== null && closedFilter.value !== '') {
          isClosed = closedFilter.value === true || closedFilter.value === 'true';
        }
      }
      const response = await getWeightNotesReport(
        props.user.userPickedHarbour,
        searchText,
        pageToFetch,
        isClosed,
        filterModel
      );
      setReportData(prev => ({
        ...prev,
        weightNotes: reset ? response : [...(prev.weightNotes || []), ...response]
      }));
      setHasMore(response.length === PAGE_SIZE);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      logError(error);
    }
  };

  const reportConfig = [
    {
      label: "Yfirlit",
      component: <Overview user={props.user} />,
      icon: "📊",
      report: {
        id: "yfirlit",
        title: "Yfirlit",
        reportDate: formatDate(new Date()),
        description: `Yfirlitsmynd byggð á ${API_PAGINATION_SIZE} nýjustu færslum landana, vigtarnótum og afla.`,
        groupBy: []
      },
    },
    {
      label: "Landanayfirlit",
      component:
        <LandingReport
          user={props.user}
          apiRef={apiRef}
          refetchLandings={props.refetchLandings}
          onLandingSelect={props.onLandingSelect}
          toggleDashboardOpen={props.toggleDashboard}
          landings={reportData.landingOverview as LandingOverview[] || []}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          loading={loading}
          page={page}
          setPage={setPage}
          fetchLandings={fetchLandings}
          hasMore={hasMore}
        />,
      icon: "🚢",
      report: {
        id: "landingOverview",
        title: "Landanir",
        reportDate: formatDate(new Date()),
        description: "Birtir yfirlit landana með frádrátti og veiðistofnum",
        groupBy: ["shipName", "buyerName"]
      },
    },
    {
      label: "Vigtarnótur",
      component:
        <WeightNoteReport
          apiRef={apiRef}
          toggleDashboardOpen={props.toggleDashboard}
          onLandingSelect={props.onLandingSelect}
          weightNotes={reportData.weightNotes as WeightNote[] || []}
          loading={loading}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          fetchWeightNotes={fetchWeightNotes}
          hasMore={hasMore}
        />,
      icon: "⚖️",
      report: {
        id: "weightNotes",
        title: "Vigtarnótur",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um vigtarnótur",
        groupBy: []
      },
    },
    {
      label: "Aflagrunnur",
      component:
        <CatchReport
          user={props.user}
          apiRef={apiRef}
          toggleDashboardOpen={props.toggleDashboard}
          onLandingSelect={props.onLandingSelect}
          catches={reportData.catch as WeightReport[] || []}
          loading={loading}
          search={search}
          setSearch={setSearch}
          page={page}
          setPage={setPage}
          fetchCatches={fetchCatches}
          hasMore={hasMore}
        />,
      icon: "🐟",
      report: {
        id: "catch",
        title: "Aflagrunnur",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um skráðan afla",
        groupBy: []
      }
    },
    {
      label: "Aflafréttir",
      component:
        <CatchNewsReport
          apiRef={apiRef}
          toggleDashboardOpen={props.toggleDashboard}
          onLandingSelect={props.onLandingSelect}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          loading={loading}
          catchNews={reportData.catchNews as CatchNews[] || []}
          hasMore={hasMore}
          page={page}
          setPage={setPage}
          fetchCatchNews={fetchCatchNews}
        />,
      icon: "📰",
      report: {
        id: "catchNews",
        title: "Aflafréttir",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um aflafréttir",
        groupBy: ["gearName", "shipName"]
      }
    },
    {
      label: "Hreyfingalisti",
      component:
        <TransactionsReport
          apiRef={apiRef}
          toggleDashboardOpen={props.toggleDashboard}
          onLandingSelect={props.onLandingSelect}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          loading={loading}
          transactions={reportData.transactions as transactions[] || []}
          fetchTransactions={fetchTransactions}
          hasMore={hasMore}
          setPage={setPage}
        />,
      icon: "📋",
      report: {
        id: "transactions",
        title: "Hreyfingalisti",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um hreyfingalista",
        groupBy: ["receiverNameSSN"]
      }
    },
    {
      label: "Samtölur fisktegunda",
      component:
        <CatchSumReport
          apiRef={apiRef}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          loading={loading}
          catchSum={reportData.catchSum as CatchSum[] || []}
          fetchCatchSum={fetchCatchSum}
        />,
      icon: "🔢",
      report: {
        id: "catchSum",
        title: "Samtölur fisktegunda",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um samtölur fisktegunda",
        groupBy: []
      }
    },
    {
      label: "Aflaverðmæti",
      component:
        <CatchValueReport
          apiRef={apiRef}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          loading={loading}
          catchValues={reportData.verdmaeti as CatchValue[] || []}
          fetchCatchValue={fetchCatchValues}
        />,
      icon: "💰",
      report: {
        id: "catchValue",
        title: "Aflaverðmæti",
        reportDate: formatDate(new Date()),
        description: "Skýrsla um aflaverðmæti",
        groupBy: ["sellerName"]
      }
    }
  ];

  const selectedReport = reportConfig[tabIndex].report || { id: "", title: "", reportDate: "", description: "" };

  // Debounce search input and reset paging for selected report
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      // Fetch data for the selected report
      const fetcher = fetchers[selectedReport.id];
      if (fetcher) {
        fetcher(1, search, true, filterModel);
      }
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedReport.id, props.user.userPickedHarbour]);

  // Fetch data when page changes (for infinite scroll)
  useEffect(() => {
    if (page === 1) return; // page 1 is handled by search effect above
    const fetcher = fetchers[selectedReport.id];
    if (fetcher) {
      fetcher(page, search, false, filterModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedReport.id]);

  // Fetch data when dateFrom/dateTo changes for reports that use dates
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    const fetcher = fetchers[selectedReport.id];
    if (fetcher) {
      fetcher(1, search, true, filterModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // whenever the tab changes, re-read columns from the active child
  useEffect(() => {
    const id = setTimeout(() => {
      if (!apiRef.current) return;

      const all = apiRef.current.getAllColumns?.() ?? [];

      // Only filterable, non-action, visible columns
      const usable = all
        .filter((c: any) => c?.filterable !== false)
        .filter((c: any) => !c?.type?.startsWith?.("actions"));

      setFilterColumns(usable as any);

      // existing groupOptions logic...
      const opts = usable.map((c: any) => ({ field: c.field, label: c.headerName ?? c.field }));
      setGroupOptions(opts);
      const defaultGroupBy = reportConfig[tabIndex]?.report?.groupBy ?? [];
      setGroupBy(defaultGroupBy);
    }, 0);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, apiRef]);

  const isClauseEmpty = (c: FilterClause): boolean => {
    const t = (c.type ?? "string") as any;
    const op = c.operator as any;
    const v = c.value as any;

    if (t === "boolean") {
      if (op === "isNull" || op === "isNotNull") return false; // valid without value
      return typeof v !== "boolean"; // must be true/false
    }

    if (t === "number") {
      if (op === "in") return !Array.isArray(v) || v.length === 0;
      return v === "" || v === null || Number.isNaN(v);
    }

    if (t === "date") {
      if (op === "between") {
        const s = v?.start as string | undefined;
        const e = v?.end as string | undefined;
        return !s && !e; // need at least one bound
      }
      return !v; // single ISO string
    }

    // string
    if (op === "in") return !Array.isArray(v) || v.length === 0;
    return !v || String(v).trim() === "";
  };

  const pruneFilter = (fp?: FilterPayload): FilterPayload => ({
    logic: fp?.logic === "or" ? "or" : "and",
    clauses: (fp?.clauses ?? []).filter((c) => !isClauseEmpty(c)),
  });


  // stable, pruned filter and key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prunedFilter = useMemo(() => pruneFilter(filterModel), [filterModel]);
  const prunedKey = useMemo(() => JSON.stringify(prunedFilter), [prunedFilter]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    const fetcher = fetchers[selectedReport.id];
    if (fetcher) {
      const payload = (prunedFilter.clauses.length > 0) ? prunedFilter : undefined;
      fetcher(1, search, true, payload);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prunedKey, selectedReport.id]);


  const toggleSendReportModal = () => {
    setOpenSendReportModal(!openSendReportModal);
  };

  const handleSideBarClick = (index: number) => {
    setTabIndex(index);
    setFilterModel({ logic: "and", clauses: [] }); // Reset filter model when changing reports
    if (reportConfig[index].report.id === "catchValue") {
      // If users switch to catch value we must reset dates since it has restrictions.
      setDateFrom(formatDate(dayjs().subtract(1, 'month').toDate()));
      setDateTo(formatDate(dayjs().toDate()));
    }
    setGroupBy([]);
  };

  const handleDownloadExcel = async () => {
    let reportDataToExport: unknown[] = [];

    switch (selectedReport.id) {
      case "landingOverview":
        reportDataToExport = reportData.landingOverview as LandingOverview[] || [];
        break;
      case "weightNotes":
        reportDataToExport = reportData.weightNotes as WeightNote[] || [];
        break;
      case "catch":
        reportDataToExport = reportData.catch as WeightReport[] || [];
        break;
      case "catchValue":
        reportDataToExport = reportData.verdmaeti as CatchValue[] || [];
        break;
      case "catchSum":
        reportDataToExport = reportData.catchSum as CatchSum[] || [];
        break;
      default:
        setExcelLoading(true);
        try {
          let allData: unknown[] = [];
          if (selectedReport.id === "catchNews") {
            allData = await getCatchNewsReport(
              props.user.userPickedHarbour,
              undefined,
              dateFrom,
              dateTo,
              filterModel
            );
          } else if (selectedReport.id === "transactions") {
            allData = await getTransactionsReport(
              props.user.userPickedHarbour,
              undefined,
              dateFrom,
              dateTo,
              prunedFilter
            );
          } else if (selectedReport.id === "catchSum") {
            allData = await getCatchSumReport(
              props.user.userPickedHarbour,
              dateFrom,
              dateTo,
              filterModel
            );
          } else if (selectedReport.id === "catchValue") {
            allData = await getCatchValueReport(
              props.user.userPickedHarbour,
              dateFrom,
              dateTo,
              filterModel
            );
          }
          reportDataToExport = allData;
        } catch (error) {
          logError(error);
          props.showSnackbar("Villa við að sækja gögn fyrir Excel", "error");
          setExcelLoading(false);
          return;
        }
        setExcelLoading(false);
        break;
    }

    if (!reportDataToExport || reportDataToExport.length === 0) {
      props.showSnackbar("Engin gögn til að sækja í Excel", "error");
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flattenObject = (obj: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          Object.entries(value).forEach(([subKey, subValue]) => {
            result[`${key}_${subKey}`] = subValue;
          });
        } else {
          result[key] = value;
        }
      });
      return result;
    };

    const cleanData = reportDataToExport.map((row) => flattenObject(row));

    // Dynamically get headers from cleaned data
    const headers = cleanData.length > 0
      ? Object.keys(cleanData[0])
      : [];

    const ExcelJS = await import('exceljs');
    const saveAs = (await import('file-saver')).default;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.columns = headers.map((key) => ({
      header: key,
      key: key,
      width: 20,
    }));

    cleanData.forEach((row) => {
      worksheet.addRow(row);
    });

    // Auto filter and style header row for UX
    worksheet.autoFilter = {
      from: 'A1',
      to: String.fromCharCode(65 + headers.length - 1) + '1',
    };
    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${selectedReport.title || "report"}.xlsx`);
  };

  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      const pdfBlob = await getReportPDF(
        selectedReport.id,
        props.user.userPickedHarbour,
        dateFrom,
        dateTo,
        prunedFilter,
        effectiveGroupBy
      );
      if (pdfBlob instanceof Blob) {
        const filename = buildFileName(
          selectedReport.title || "skyrsla",
          props.user.userPickedHarbourName,
          ".pdf"
        );
        const saveAs = (await import('file-saver')).default;
        saveAs(pdfBlob, filename);
      } else {
        props.showSnackbar("PDF skýrsla fannst ekki", "error");
      }
      setPdfLoading(false);
    } catch (error) {
      setPdfLoading(false);
      logError(error);
      props.showSnackbar("Villa við að sækja PDF skýrslu", "error");
    }
  };

  // Same i18n keys as FiltersDialog
  const OP_ISL: Record<string, string> = {
  // string
    contains: "Inniheldur",
    equals: "Jafnt og",
    startsWith: "Byrjar á",
    endsWith: "Endar á",
    not: "Er ekki",
    in: "Er einhver af",
    // number
    eq: "Jafnt og",
    gt: "Stærra en",
    gte: "Stærra eða jafnt",
    lt: "Minna en",
    lte: "Minna eða jafnt",
    // date
    on: "Á",
    after: "Eftir",
    onOrAfter: "Á eða eftir",
    before: "Fyrir",
    onOrBefore: "Á eða fyrir",
    between: "Milli dagsetninga",
    // boolean
    is: "Er",
    isNot: "Er ekki",
    isNull: "Er tómt",
    isNotNull: "Er ekki tómt",
  };
  const isValidIS = (s?: string) => !!s && dayjs(s, "DD.MM.YYYY", true).isValid();
  const isValidISO = (s?: string) => !!s && dayjs(s, "YYYY-MM-DD", true).isValid();

  const fmtChipDate = (s?: string) => {
    if (!s) return "—";
    if (isValidIS(s))  return dayjs(s, "DD.MM.YYYY", true).format("DD.MM.YYYY");
    if (isValidISO(s)) return dayjs(s, "YYYY-MM-DD", true).format("DD.MM.YYYY");
    // last-resort fallback
    const d = dayjs(s);
    return d.isValid() ? d.format("DD.MM.YYYY") : "—";
  };
  return (
    <div style={{ position: "relative", height: "calc(100vh - 56px)", width: "100vw", overflow: "hidden" }}>
      {/* Send report modal rendered */}
      <SendReportModal
        user={props.user}
        open={openSendReportModal}
        toggleOpen={() => setOpenSendReportModal(!openSendReportModal)}
        showSnackbar={props.showSnackbar}
        selectedReport={selectedReport}
        isMobileView={false}
        filterModel={prunedFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        groupBy={effectiveGroupBy}
      />
      {/* Filters dialog */}
      <FiltersDialog
        open={filtersOpen}
        onClose={(applied) => {
          setFiltersOpen(false);
          setFilterModel(applied);
        }}
        columns={filterColumns}
        value={filterModel}
        initialFocusIndex={focusIndex}
      />
      {/* Sidebar */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 200,
          transition: "width 0.2s",
          background: "linear-gradient(135deg, #23272f 0%, #374151 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          paddingTop: 24,
          boxShadow: "2px 0 8px rgba(30,41,59,0.10)",
          borderRight: "1px solid #2d3748",
          minHeight: 0,
          zIndex: 2,
        }}
      >
        {/* Back Arrow */}
        <div
          onClick={() => props.toggleDashboard(true)}
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            padding: "12px 18px",
            background: "rgba(55,65,81,0.18)",
            borderRadius: 8,
            marginBottom: 20,
            width: "100%",
            color: "#e5e7eb",
            transition: "background 0.2s",
          }}
          title="Fara til baka á yfirlit"
        >
          <span style={{ fontSize: 26, color: "#e5e7eb" }}>←</span>
          <span style={{ marginLeft: 14, fontWeight: 600, color: "#e5e7eb" }}>Til baka</span>
        </div>
        {reportConfig.map((report, idx) => (
          <div
            key={report.label}
            onClick={() => handleSideBarClick(idx)}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              padding: "12px 18px",
              background: tabIndex === idx
                ? "linear-gradient(90deg, #374151 60%, #23272f 100%)"
                : "transparent",
              borderRadius: 8,
              marginBottom: 10,
              width: "100%",
              color: tabIndex === idx ? "#fff" : "#cbd5e1",
              boxShadow: tabIndex === idx ? "0 2px 8px rgba(30,41,59,0.10)" : "none",
              fontWeight: tabIndex === idx ? 700 : 500,
              transition: "background 0.2s, color 0.2s",
            }}
          >
            <span style={{ fontSize: 26, color: tabIndex === idx ? "#38bdf8" : "#94a3b8" }}>{report.icon}</span>
            <span style={{
              marginLeft: 14,
              fontWeight: 600,
              color: tabIndex === idx ? "#fff" : "#cbd5e1",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 16,
            }}>
              {report.label}
            </span>
          </div>
        ))}
      </div>
      {/* Main Content */}
      <div
        style={{
          position: "relative",
          marginLeft: 200,
          height: "100%",
          transition: "margin-left 0.2s",
          display: "flex",
          flexDirection: "column",
          padding: "20px 24px",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #23272f 0%, #374151 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #e3e8ee 100%)",
          minHeight: 0,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            borderBottom: theme.palette.mode === "dark"
              ? "1px solid #374151"
              : "1px solid #e0e0e0",
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              width: "100%",
              gap: 12,
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: 0,
                flex: "1 1 220px",
                boxSizing: "border-box",
                maxWidth: "100%",
              }}
            >
              <h1
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  margin: 0,
                  color: theme.palette.mode === "dark"
                    ? "#e5e7eb"
                    : "#1a202c",
                  letterSpacing: "-1px",
                  wordBreak: "break-word",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {reportConfig[tabIndex].label}
              </h1>
              <span
                style={{
                  fontSize: 16,
                  color: theme.palette.mode === "dark"
                    ? "#94a3b8"
                    : "#64748b",
                  fontWeight: 400,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {selectedReport.description || ""}
              </span>
              <Box sx={{ mb: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {(filterModel.clauses ?? []).map((c, idx) => {
                  const col = filterColumns.find(fc => fc.field === c.field);
                  const header = col?.headerName || c.field;
                  const op = OP_ISL[c.operator ?? ""] ?? c.operator ?? "";

                  let v = "";
                  if (c.type === "date") {
                    if (c.operator === "between" && typeof c.value === "object" && c.value) {
                      const vv = c.value as any;
                      v = `${fmtChipDate(vv.start)} → ${fmtChipDate(vv.end)}`;
                    } else if (typeof c.value === "string") v = fmtChipDate(c.value);
                  } else if (Array.isArray(c.value)) v = c.value.join(" eða ");
                  else if (c.value !== undefined && c.value !== null) v = String(c.value);

                  const label = `${header} ${op}${v ? ` ${v}` : ""}`;

                  return (
                    <Chip
                      key={`${c.field}-${idx}`}
                      label={label}
                      onClick={() => {
                        setFocusIndex(idx);
                        setFiltersOpen(true);
                      }}
                      onDelete={() => {
                        const next = {
                          ...filterModel,
                          clauses: filterModel.clauses.filter((_, i) => i !== idx),
                        };
                        setFilterModel(next);
                      }}
                      variant="outlined"
                      sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
                    />
                  );
                })}
              </Box>

            </div>
            {/* Group-by control */}
            {selectedReport.id !== "yfirlit" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                  justifyContent: "flex-end",
                  minWidth: 0,
                  flex: "1 1 320px",
                  boxSizing: "border-box",
                  maxWidth: "100%",
                  overflow: "visible",
                }}
              >
                <button
                  onClick={() => setFiltersOpen(true)}
                  style={{
                    background: theme.palette.mode === "dark"
                      ? "linear-gradient(90deg, #6b7280 0%, #374151 100%)"
                      : "linear-gradient(90deg, #94a3b8 0%, #cbd5e1 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 24px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: theme.palette.mode === "dark"
                      ? "0 2px 8px rgba(100,116,139,0.18)"
                      : "0 2px 8px rgba(100,116,139,0.08)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span role="img" aria-label="filters">🧰</span>
                    Síur{activeCount ? ` (${activeCount})` : ""}
                </button>
                <button
                  onClick={toggleSendReportModal}
                  style={{
                    background: selectedReport.id === "yfirlit"
                      ? (theme.palette.mode === "dark"
                        ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                        : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)")
                      : (theme.palette.mode === "dark"
                        ? "linear-gradient(90deg, #2563eb 0%, #0ea5e9 100%)"
                        : "linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)"),
                    color: selectedReport.id === "yfirlit"
                      ? (theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#64748b")
                      : "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 24px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: selectedReport.id === "yfirlit" ? "not-allowed" : "pointer",
                    boxShadow: selectedReport.id === "yfirlit"
                      ? "none"
                      : (theme.palette.mode === "dark"
                        ? "0 2px 8px rgba(59,130,246,0.18)"
                        : "0 2px 8px rgba(59,130,246,0.08)"),
                    transition: "background 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    opacity: selectedReport.id === "" ? 0.6 : 1,
                    minWidth: 80,
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <span role="img" aria-label="send">📤</span>
                  Senda
                </button>
                <button
                  onClick={handleDownloadPDF}
                  style={{
                    background:
                      pdfLoading
                        ? (theme.palette.mode === "dark"
                          ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                          : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)")
                        : (theme.palette.mode === "dark"
                          ? "linear-gradient(90deg, #ef4444 0%, #2563eb 100%)"
                          : "linear-gradient(90deg, #ef4444 0%, #3b82f6 100%)"),
                    color:
                      pdfLoading
                        ? (theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#64748b")
                        : "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 24px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor:
                      pdfLoading ? "not-allowed" : "pointer",
                    boxShadow:
                      pdfLoading
                        ? "none"
                        : (theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(239,68,68,0.18)"
                          : "0 2px 8px rgba(239,68,68,0.08)"),
                    transition: "background 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    opacity:
                      pdfLoading ? 0.6 : 1,
                    minWidth: 80,
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    position: "relative",
                  }}
                  disabled={
                    pdfLoading}
                >
                  <span role="img" aria-label="download-pdf">📄</span>
                  {pdfLoading ? (
                    <span style={{
                      marginLeft: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                    }}>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          border: "2px solid #fff",
                          borderTop: "2px solid #ef4444",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          marginRight: 8,
                          display: "inline-block",
                        }}
                      />
                      Sæki PDF...
                    </span>
                  ) : (
                    "PDF"
                  )}
                </button>
                <button
                  onClick={handleDownloadExcel}
                  style={{
                    background:
                      excelLoading
                        ? (theme.palette.mode === "dark"
                          ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                          : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)")
                        : (theme.palette.mode === "dark"
                          ? "linear-gradient(90deg, #22c55e 0%, #2563eb 100%)"
                          : "linear-gradient(90deg, #22c55e 0%, #3b82f6 100%)"),
                    color:
                      excelLoading
                        ? (theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#64748b")
                        : "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 24px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor:
                      excelLoading ? "not-allowed" : "pointer",
                    boxShadow:
                      excelLoading
                        ? "none"
                        : (theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(34,197,94,0.18)"
                          : "0 2px 8px rgba(34,197,94,0.08)"),
                    transition: "background 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    opacity:
                      excelLoading ? 0.6 : 1,
                    minWidth: 80,
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    position: "relative",
                  }}
                  disabled={
                    excelLoading}
                >
                  <span role="img" aria-label="download-excel">📊</span>
                  {excelLoading ? (
                    <span style={{
                      marginLeft: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                    }}>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          border: "2px solid #fff",
                          borderTop: "2px solid #22c55e",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          marginRight: 8,
                          display: "inline-block",
                        }}
                      />
                      Sæki Excel...
                    </span>
                  ) : (
                    "Excel"
                  )}
                </button>
                {/* Spinner keyframes */}
                <style>
                  {`
                  @keyframes spin {
                  0% { transform: rotate(0deg);}
                  100% { transform: rotate(360deg);}
                  }
                `}
                </style>
                <Box sx={{ width: 320 }}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disablePortal={false}
                    options={groupOptions}
                    value={selectedGroupOptions}
                    onChange={(_e, selected) => {
                      if (selected.length > 3) return;
                      setGroupBy(selected.map(s => s.field));
                    }}
                    getOptionLabel={(opt) => opt.label}
                    getOptionDisabled={(option) => {
                      const alreadySelected = groupBy.includes(option.field);
                      return groupBy.length >= 3 && !alreadySelected;
                    }}
                    renderTags={(value, getTagProps) => (
                      <Box sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        gap: 0.5,
                        maxWidth: "100%",
                        minHeight: 28,
                        alignItems: "center",
                        "::-webkit-scrollbar": { height: 6 },
                      }}>
                        {value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.field}
                            label={option.label}
                            size="small"
                            sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}
                          />
                        ))}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hópa skjal eftir"
                        placeholder="Veldu dálka"
                        size="small"
                        variant="outlined"
                        helperText={`${groupBy.length}/3`}
                        FormHelperTextProps={{ sx: { minHeight: 18 } }}
                      />
                    )}
                    limitTags={3}
                    ListboxProps={{ style: { maxHeight: 260 } }}
                  />
                </Box>
              </div>
            )}

          </div>
        </header>
        <main
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            background: theme.palette.mode === "dark"
              ? "#23272f"
              : "#fff",
            borderRadius: 16,
            boxShadow: theme.palette.mode === "dark"
              ? "0 4px 24px rgba(30,41,59,0.18)"
              : "0 4px 24px rgba(30,41,59,0.06)",
          }}
        >
          {reportConfig[tabIndex].component}
        </main>
      </div>
    </div>
  );
};

export default Reports;
